import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { useIOSDetection } from "@/hooks/use-ios-detection";
import { MediaUnlocker } from "@/lib/mediaUnlocker";

export function LoadingPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isIOS } = useIOSDetection();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isLoadingVideo, setIsLoadingVideo] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  
  // Set the loading animation URL directly
  useEffect(() => {
    const loadingVideoUrl = "https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/final_outputIntro12.10.25.mp4";
    
    setIsLoadingVideo(false);
    setVideoError(false);
    setVideoUrl(loadingVideoUrl);
    
    console.log("Using loading animation:", loadingVideoUrl);
  }, []);
  
  useEffect(() => {
    // Navigate after a delay if video fails to load
    const timer = setTimeout(() => {
      if (!isAnimationComplete) {
        handleNavigateAway();
      }
    }, 8000); // 8 seconds fallback
    
    return () => clearTimeout(timer);
  }, [navigate, isAnimationComplete]);

  const handleVideoLoaded = () => {
    console.log("Video loaded and ready to play");
    setIsVideoLoaded(true);
    // Start fade in animation
    setTimeout(() => {
      setFadeIn(true);
    }, 100);
  };

  const handleVideoEnd = () => {
    console.log("Video playback ended");
    handleNavigateAway();
  };

  const handleNavigateAway = () => {
    if (isAnimationComplete) return;
    
    setIsAnimationComplete(true);
    // Start fade out animation
    setFadeOut(true);
    
    // Navigate after fade out completes
    setTimeout(() => {
      navigate("/");
    }, 500); // 500ms for fade out
  };

  const handleVideoError = () => {
    console.error("Video playback error detected");
    setVideoError(true);
    // Don't navigate immediately, let the timeout handle it
  };

  const handleVideoCanPlay = async () => {
    console.log("Video can start playing");
    const video = videoRef.current;
    if (!video) return;
    
    try {
      // For iOS, check if media is unlocked and set muted state accordingly
      const mediaUnlocked = MediaUnlocker.instance().isUnlocked();
      
      if (mediaUnlocked && isIOS) {
        video.muted = false;
        video.volume = 1;
        console.log("Starting video with sound (media unlocked)");
      } else {
        video.muted = true;
        console.log("Starting video muted");
      }
      
      // Explicitly play the video
      await video.play();
      console.log("Video playback started successfully");
    } catch (error) {
      console.error("Error playing video:", error);
      // If play fails, try with muted
      try {
        video.muted = true;
        await video.play();
        console.log("Video started in muted fallback mode");
      } catch (fallbackError) {
        console.error("Fallback video play failed:", fallbackError);
        setVideoError(true);
      }
    }
  };

  return (
    <div className="h-screen w-screen flex flex-col items-center justify-center bg-black relative overflow-hidden fixed inset-0">
      {/* Glass blur overlay for fade effects - hide if error to show fallback */}
      {!videoError && (
        <div 
          className={`fixed inset-0 z-30 pointer-events-none transition-all duration-500 ${
            fadeIn ? 'opacity-0 backdrop-blur-0' : 'opacity-100 backdrop-blur-xl'
          } ${fadeOut ? 'opacity-100 backdrop-blur-xl' : ''}`}
          style={{
            background: fadeOut || !fadeIn ? 'rgba(0, 0, 0, 0.8)' : 'transparent',
            backdropFilter: fadeOut || !fadeIn ? 'blur(20px) saturate(1.5)' : 'blur(0px)',
            WebkitBackdropFilter: fadeOut || !fadeIn ? 'blur(20px) saturate(1.5)' : 'blur(0px)',
          }}
        />
      )}
      
      {videoUrl && !videoError ? (
        <div className={`w-screen h-screen fixed inset-0 bg-black transition-opacity duration-500 ${
          isVideoLoaded && fadeIn ? 'opacity-100' : 'opacity-0'
        }`}>
          <video
            ref={videoRef}
            className="w-screen h-screen object-cover bg-black"
            playsInline
            muted
            preload="auto"
            crossOrigin="anonymous"
            onLoadedData={handleVideoLoaded}
            onCanPlay={handleVideoCanPlay}
            onEnded={handleVideoEnd}
            onError={handleVideoError}
            style={{
              height: '100vh',
              width: '100vw',
              backgroundColor: '#000000',
              objectFit: 'cover'
            }}
          >
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      ) : (
        <div 
          className="flex flex-col items-center justify-center z-20"
          style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}
        >
          {isLoadingVideo || !isVideoLoaded ? (
            <Loader2 className="w-12 h-12 text-white animate-spin" />
          ) : (
            <div className="text-white text-2xl font-semibold tracking-tight leading-tight animate-fade-in">
              Block Bookster
            </div>
          )}
        </div>
      )}
    </div>
  );
}